/**
 * Frontend Permission Constants
 *
 * Mirror of backend permission registry for use in React components.
 * Import from this file to check permissions in UI.
 *
 * Usage:
 * ```tsx
 * import { PERMISSIONS } from '@/constants/permissions';
 *
 * <PermissionGate permission={PERMISSIONS.MANAGE_USERS}>
 *   <Button>Invite User</Button>
 * </PermissionGate>
 * ```
 */

export const PERMISSIONS = {
  // ===== USER MANAGEMENT =====
  MANAGE_USERS: 'manage_users',
  INVITE_USERS: 'invite_users',
  REMOVE_USERS: 'remove_users',
  EDIT_USER_ROLES: 'edit_user_roles',
  VIEW_USERS: 'view_users',

  // ===== ROLE & PERMISSION MANAGEMENT =====
  MANAGE_ROLES: 'manage_roles',

  // ===== ATTACHMENTS =====
  MANAGE_ATTACHMENTS: 'manage_attachments',

  // ===== ORGANIZATION KNOWLEDGE =====
  MANAGE_KNOWLEDGE: 'manage_knowledge',

  // ===== ORGANIZATION SETTINGS =====
  MANAGE_ORGANIZATION_SETTINGS: 'manage_organization_settings',

  // ===== ROROS PERMISSIONS =====
  ROROS_DOMAINS_VIEW: 'roros.domains.view',
  ROROS_DOMAINS_MANAGE: 'roros.domains.manage',
  ROROS_ARTIFACTS_CREATE: 'roros.artifacts.create',
  ROROS_ARTIFACTS_EDIT: 'roros.artifacts.edit',
  ROROS_ARTIFACTS_DELETE: 'roros.artifacts.delete',
  ROROS_ACTORS_MANAGE: 'roros.actors.manage',
  ROROS_ADOPTION_LEVEL_CHANGE: 'roros.adoption-level.change',
  ROROS_ROLE_CONTRACTS_MANAGE: 'roros.role-contracts.manage',
  ROROS_CONTROL_TOWER_VIEW: 'roros.control-tower.view',
  ROROS_CONTROL_TOWER_MANAGE: 'roros.control-tower.manage',
  ROROS_CONTROL_TOWER_DELETE: 'roros.control-tower.delete',
  ROROS_DECISIONS_CREATE: 'roros.decisions.create',
  ROROS_DECISIONS_APPROVE: 'roros.decisions.approve',
  ROROS_GOVERNANCE_MANAGE: 'roros.governance.manage',
  ROROS_OPERATING_MODE_CHANGE: 'roros.operating-mode.change',
  ROROS_CADENCE_MANAGE: 'roros.cadence.manage',
  ROROS_OVERARCHING_MANAGE: 'roros.overarching.manage',
  ROROS_OVERARCHING_VIEW: 'roros.overarching.view',
  ROROS_SKILLS_MANAGE: 'roros.skills.manage',
  ROROS_SETTINGS_MANAGE: 'roros.settings.manage',
  ROROS_STRUCTURE_MANAGE: 'roros.structure.manage',
  // AC-FW2-FU1-1: Meetings list page permission (mirrors backend roros.meetings.read)
  ROROS_MEETINGS_READ: 'roros.meetings.read',
  // Wave 3b Packet B: Verification Inbox permissions
  ROROS_VERIFICATION_INBOX_VIEW: 'roros.verification-inbox.view',
  ROROS_VERIFICATION_INBOX_DECIDE: 'roros.verification-inbox.decide',
  // F-SKILL-1 Packet B1: Agent Skills library (mirrors backend roros.agent-skills.*)
  ROROS_AGENT_SKILLS_VIEW: 'roros.agent-skills.view',
  ROROS_AGENT_SKILLS_MANAGE: 'roros.agent-skills.manage',
  // VP Feedback Loop: Notification center (per-user, own notifications only)
  ROROS_NOTIFICATIONS_VIEW: 'roros.notifications.view',
} as const;

/**
 * Permission type
 */
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission metadata for UI
 */
export interface PermissionMetadata {
  label: string;
  description: string;
  category: string;
  granularOf?: Permission;
}

/**
 * Permission metadata for display in UI
 */
export const PERMISSION_METADATA: Record<Permission, PermissionMetadata> = {
  // User Management
  [PERMISSIONS.MANAGE_USERS]: {
    label: 'Manage Users',
    description: 'Full access to invite, remove, and manage organization members',
    category: 'User Management',
  },
  [PERMISSIONS.INVITE_USERS]: {
    label: 'Invite Users',
    description: 'Send invitations to new members',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.REMOVE_USERS]: {
    label: 'Remove Users',
    description: 'Remove members from the organization',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.EDIT_USER_ROLES]: {
    label: 'Edit User Roles',
    description: 'Change member roles and permissions',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.VIEW_USERS]: {
    label: 'View Users',
    description: 'View the member list and their details',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },

  // Role Management
  [PERMISSIONS.MANAGE_ROLES]: {
    label: 'Manage Roles',
    description: 'Create, edit, and delete custom roles and permissions',
    category: 'Role Management',
  },

  // Attachments
  [PERMISSIONS.MANAGE_ATTACHMENTS]: {
    label: 'Manage Attachments',
    description: 'Full access to upload, view, and delete organization attachments',
    category: 'Organization Content',
  },

  // Organization Knowledge
  [PERMISSIONS.MANAGE_KNOWLEDGE]: {
    label: 'Manage Company Knowledge',
    description: 'Full access to upload, view, and delete company knowledge documents',
    category: 'Organization Content',
  },

  // Organization Settings
  [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]: {
    label: 'Manage Organization Settings',
    description: 'Full access to manage organization profile, brand guidelines, and settings',
    category: 'Organization Settings',
  },

  // Rocket Routine OS
  [PERMISSIONS.ROROS_DOMAINS_VIEW]: {
    label: 'View ROROS Domains',
    description: 'View domains and their artifacts',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_DOMAINS_MANAGE]: {
    label: 'Manage ROROS Domains',
    description: 'Activate/deactivate domains and manage domain settings',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_CREATE]: {
    label: 'Create Artifacts',
    description: 'Create outcomes, metrics, principles, routines, initiatives, knowledge, and learning items',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_EDIT]: {
    label: 'Edit Artifacts',
    description: 'Edit existing artifacts',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ARTIFACTS_DELETE]: {
    label: 'Delete Artifacts',
    description: 'Archive or delete artifacts',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ACTORS_MANAGE]: {
    label: 'Manage Actors',
    description: 'Create and manage actors, assign domains',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ADOPTION_LEVEL_CHANGE]: {
    label: 'Change Adoption Level',
    description: 'Change AI operator adoption levels',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_ROLE_CONTRACTS_MANAGE]: {
    label: 'Manage Role Contracts',
    description: 'Create, edit, and activate role contracts',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_VIEW]: {
    label: 'View Control Tower',
    description: 'View work items, overview, and verification inbox',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_MANAGE]: {
    label: 'Manage Control Tower',
    description: 'Create work items and manage transitions',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_CONTROL_TOWER_DELETE]: {
    label: 'Delete Work Items',
    description: 'Permanently delete work items (with all sub-items, history, links, and QC reports)',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_DECISIONS_CREATE]: {
    label: 'Create Decisions',
    description: 'Create and edit decision notes',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_DECISIONS_APPROVE]: {
    label: 'Approve Decisions',
    description: 'Approve or reject decision notes',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_GOVERNANCE_MANAGE]: {
    label: 'Manage Governance',
    description: 'Manage constitution, exploration zones, and decision rights',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_OPERATING_MODE_CHANGE]: {
    label: 'Change Operating Mode',
    description: 'Change the operating mode (founder/growth/scaling)',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_CADENCE_MANAGE]: {
    label: 'Manage Cadence',
    description: 'Manage cadence schedules and create reviews',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_OVERARCHING_MANAGE]: {
    label: 'Manage Overarching Content',
    description: 'Edit company profile, values, principles, USPs, and other strategic content',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_OVERARCHING_VIEW]: {
    label: 'View Overarching Content',
    description: 'View company profile, values, principles, USPs, and other strategic content',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_SKILLS_MANAGE]: {
    label: 'Manage Skills',
    description: 'Create, edit, and delete AI operator skills',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_SETTINGS_MANAGE]: {
    label: 'Manage ROROS Settings',
    description: 'Manage ROROS tenant settings (system settings page)',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_STRUCTURE_MANAGE]: {
    label: 'Manage Structure Settings',
    description: 'Manage domains, actors, operating mode and role contracts (Owner/Admin only)',
    category: 'Rocket Routine OS',
  },
  // AC-FW2-FU1-1
  [PERMISSIONS.ROROS_MEETINGS_READ]: {
    label: 'View Meetings',
    description: 'View meetings list and meeting details',
    category: 'Rocket Routine OS',
  },
  // Wave 3b Packet B: Verification Inbox
  [PERMISSIONS.ROROS_VERIFICATION_INBOX_VIEW]: {
    label: 'View Verification Inbox',
    description: 'View cascade submission inbox entries (Owner/Admin)',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_VERIFICATION_INBOX_DECIDE]: {
    label: 'Decide Verification Inbox',
    description: 'Approve, reject, or request ad-hoc review on inbox entries (Owner/Admin)',
    category: 'Rocket Routine OS',
  },
  // VP Feedback Loop — Notification center
  [PERMISSIONS.ROROS_NOTIFICATIONS_VIEW]: {
    label: 'View Notifications',
    description: 'View own in-app notifications (all org members)',
    category: 'Rocket Routine OS',
  },
  // F-SKILL-1 Packet B1 — Agent Skills permissions
  [PERMISSIONS.ROROS_AGENT_SKILLS_VIEW]: {
    label: 'View Agent Skills',
    description: 'Browse and search the skills library',
    category: 'Rocket Routine OS',
  },
  [PERMISSIONS.ROROS_AGENT_SKILLS_MANAGE]: {
    label: 'Manage Agent Skills',
    description: 'Create, edit, and delete custom agent skills',
    category: 'Rocket Routine OS',
  },
};

/**
 * Get permissions grouped by category for UI display
 */
export function getPermissionsByCategory(): Record<string, Permission[]> {
  const grouped: Record<string, Permission[]> = {};

  for (const [permission, metadata] of Object.entries(PERMISSION_METADATA)) {
    const category = metadata.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission as Permission);
  }

  return grouped;
}

/**
 * Format permission string to human-readable label
 */
export function formatPermissionLabel(permission: string): string {
  return PERMISSION_METADATA[permission as Permission]?.label || permission;
}
